import React, { useState } from 'react'
import { Box, FormControl, TextField, Button, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { useForm, Controller } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import DatePicker, { registerLocale } from 'react-datepicker'
import ja from 'date-fns/locale/ja'
import 'react-datepicker/dist/react-datepicker.css'
// components
import { BackButton } from '~/src/components/atoms/Button/BackButton/BackButton'
import { ProtectedPage } from '~/src/components/templates'
import { CardWithActionButton } from '~/src/components/organisms'
import { useMessage } from '~/src/hooks'
import { TNotificationItem, TNotificationBody } from '~/src/repositories/Notification'
import { notificationRepository } from '~/src/repositories'
import { formatDate } from '~/src/libs'

const notificationSchema = yup.object({
  date: yup.date().required(),
  message: yup.string().required(),
})

/**
 * NotificationCreate Page
 * @param __namedParameters
 * @returns JSX.Element
 * @category ReactComponents
 */
export const NotificationCreate: React.VFC = (): JSX.Element => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()

  const [loading, setLoading] = useState<boolean>(false)
  registerLocale('ja', ja)
  const { handleOpenWithErrorMessage, handleOpenWithSuccessMessage } = useMessage()
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<TNotificationBody>({
    resolver: yupResolver(notificationSchema),
  })

  const textFieldStyle = {
    '& label': {
      fontSize: '1rem',
    },
    '& .MuiOutlinedInput-root': {
      fontSize: '1rem',
      lineHeight: '24px',
    },
    '& .MuiOutlinedInput-input': {
      fontSize: '1rem',
      lineHeight: '24px',
      paddingY: '8px',
      placeholder: {
        fontSize: '1rem',
      },
    },
  }

  /**
   * createHandler
   * @param v {TNotificationItem}
   */
  const createHandler = async (v: TNotificationBody) => {
    if (!v.date || !v.message) {
      return handleOpenWithErrorMessage('select user')
    }
    setLoading(true)
    try {
      const payload: TNotificationBody = {
        date: formatDate(v.date, 'yyyy-MM-dd'),
        message: v.message,
      }

      await notificationRepository.postNotification(payload)
      handleOpenWithSuccessMessage('Success!!')
      navigate('/admin/news/list', { replace: true })
    } catch (e: any) {
      handleOpenWithErrorMessage(e.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <ProtectedPage pageTitle={t('page_title.news_create')}>
      <BackButton location={location} />
      <form onSubmit={handleSubmit(createHandler)}>
        <CardWithActionButton
          buttonText={t('notification.create_save')}
          loading={loading}
          cardContentPadding="16px"
          buttonSize="large"
        >
          <Box
            display={'flex'}
            flexDirection={'row'}
            alignItems={'center'}
            component="dl"
            marginTop={0}
            marginBottom={2.5}
          >
            <Box width={'146px'} component="dt">
              <Typography variant="overline" color="#000" fontSize="14px">
                {t('notification.date')}
              </Typography>
            </Box>
            <Box component="dd" width={'100%'} marginLeft={4}>
              <FormControl fullWidth>
                <Controller
                  control={control}
                  name={'date'}
                  render={({ field: { onChange, value } }) => (
                    <DatePicker
                      dateFormat="yyyy-MM-dd"
                      onChange={onChange}
                      selected={value as unknown as Date}
                      locale="ja"
                      placeholderText="20XX/XX/XX"
                      withPortal
                      customInput={
                        <TextField
                          sx={{
                            width: '260px',
                            ...textFieldStyle,
                          }}
                          size={'small'}
                          placeholder="20XX/XX/XX"
                          error={Boolean(errors.date)}
                          helperText={errors.date && errors.date.message}
                          {...register('date')}
                        />
                      }
                    />
                  )}
                />
              </FormControl>
            </Box>
          </Box>
          <Box display={'flex'} flexDirection={'row'} alignItems={'top'} component="dl" margin={0}>
            <Box width={'146px'} component="dt">
              <Typography variant="overline" color="#000" fontSize="14px">
                {t('notification.message')}
              </Typography>
            </Box>
            <Box component="dd" width={'100%'} marginLeft={4}>
              <FormControl fullWidth>
                <TextField
                  multiline
                  size={'small'}
                  placeholder={t('notification.message_placeholder')}
                  {...register('message')}
                  error={Boolean(errors.message)}
                  helperText={errors.message && errors.message.message}
                  sx={{
                    ...textFieldStyle,
                    '& .MuiOutlinedInput-root': {
                      paddingY: 0,
                    },
                  }}
                />
              </FormControl>
            </Box>
          </Box>
        </CardWithActionButton>
      </form>
    </ProtectedPage>
  )
}
